/**
 * @fileoverview A gardener's public garden page. No account needed.
 *
 * What "Public" points at, top to bottom (the owner's approved mock; the
 * sections are gardenPageSections): a full-width cover photo under a dark
 * fade (her pick in Profile > Public pages, else her newest public photo)
 * with her profile photo, "Amy's garden", "@amy · zone 7b" (never the town,
 * ZIP or notes) and a Share button; her season in numbers as pills ("5
 * plants", "2 beds", "First harvest Jul 13", each only when it is there) and
 * her social links; "Latest share", the words of her newest shared post with
 * its day and plant (left out when there is none); her public plants first,
 * as a grid of cards with a stage chip, scrolling through a plant's photos
 * when it has several (a leaf tile without any); then her public beds as a
 * row that scrolls inside itself, each card its own photo scroller (the bed's
 * picture, then the latest photo of each public plant in it, with days); her
 * season review's picture once she has sent one with its Public switch on; then
 * "Grow your own like Amy · Start your garden free", crediting her garden
 * (gardenPageParts GardenInvite, useGardenCredit); and, last, "More gardens on
 * Grow", a link to the public listing (GardensPage, /gardens). The season
 * pills scroll to the beds and plants sections. Each bed and plant opens
 * its own page under the address (GardenItemPage). Every share page's "Grown by" line leads
 * here, so a visitor who was sent one photograph can find the rest the
 * gardener chose to show. Opening the page counts one visit per browser
 * (gardens getGarden, getGardenBySlug).
 *
 * Two addresses lead here: /g/<code>, the random code every gardener has,
 * and /@<slug>, the address a gardener may choose (grow.gardengameplan.com/@amy).
 * A code whose gardener has an address moves the visitor on to it, in place
 * of the code in their history; so does an address typed in capitals or
 * with accents (/@Begoña moves to /@begona).
 *
 * React Router 6 matches whole segments only, so "/@:slug" cannot be a
 * route: App gives this page "/:handle", every one-segment path no named
 * page claims, and the page takes only those starting with "@". Anything
 * else (/amy, a typo) goes where the catch-all sends any unknown path:
 * Today, or sign-in first. So does an address no one has.
 *
 * Where: the routes /g/:code and /:handle (App.tsx), public, outside the
 * Layout.
 * API: GET /gardens/:code or GET /gardens/by-slug/:slug (getGarden,
 * getGardenBySlug; ?count=0 once counted).
 * Settings: the gardener's chosen address (slug), "Show me as" and Public
 * switches, all the backend's to apply.
 * Words (en/es): gardensPage.moreGardens, gardenPage.page.* (missing, unavailable, empty, count.*,
 * seasonNumbers, inviteTitleOf, inviteBodyNew, inviteStart, and the sections'
 * own, see gardenPageSections), seasonReview.page.title, sitewide.loading.
 * Watch out: a garden moved on from /g/:code arrives with the page in
 * router state, so the new address does not ask the server a second time.
 * A garden that could not be read on our side (a 500, no connection; D31,
 * lib/serverFailure) says the standard sentence, reported to Sentry, with the
 * support address as a link, and is not sent on to /today as an address no
 * one has; a 410 stays gardenPage.page.unavailable.
 *
 * @module GardenPage
 */

import React from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  gardenPathFor,
  getGarden,
  getGardenBySlug,
  type PublicGarden,
} from '@/shared/api/gardens';
import Spinner from '@/shared/components/ui/Spinner';
import { GardenFrame, GardenInvite, LargePicture } from './gardenPageParts';
import {
  BedRail, GARDEN_COLUMN_CLASS, GardenHero, LatestShare, PLANTS_SECTION_ID, PlantGrid,
} from './gardenPageSections';
import { DatePartsContext, datePartsOf, useGardenCredit } from './lib/gardenPage';
import { isUnavailable } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';

/**
 * The page at /g/:code or /@:slug (App's /:handle): the cover and the
 * gardener's name, zone and season, their latest share, public beds and
 * public plants, and the invitation.
 */
export const GardenPage: React.FC = () => {
  const { code = '', handle } = useParams<{ code: string; handle: string }>();
  const { reactTranslate: t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  /** At /:handle, a path with no "@" in front: not a garden, an unknown page. */
  const notAHandle = handle !== undefined && !handle.startsWith('@');
  /** At /@:slug, the address as it is in the path, without its "@"; undefined at /g/:code. */
  const typedSlug = handle !== undefined && !notAHandle ? handle.slice(1) : undefined;
  /** The address to look up, as typed in the path: the backend folds it, lower-cases it and says whether it could be one (404 when not). */
  const slug = typedSlug;
  /** A path with no "@" is never asked about; any other the backend answers. */
  const notAnAddress = notAHandle;
  // A visitor moved on from /g/:code arrives with the page already loaded.
  const handed = (location.state as { garden?: PublicGarden } | null)?.garden ?? null;
  const [garden, setGarden] = React.useState<PublicGarden | null>(handed);
  const [missing, setMissing] = React.useState(false);
  /** The gardener is suspended: the page says only that it is unavailable. */
  const [unavailable, setUnavailable] = React.useState(false);
  /** What the read threw when it failed on our side (a 500, no connection); null otherwise. */
  const [ours, setOurs] = React.useState<unknown>(null);

  React.useEffect(() => {
    if (notAnAddress) return undefined;
    if (handed && slug && handed.slug === slug) {
      setGarden(handed);
      setMissing(false);
      return undefined;
    }
    let cancelled = false;
    setGarden(null);
    setMissing(false);
    setUnavailable(false);
    setOurs(null);
    (slug !== undefined ? getGardenBySlug(slug) : getGarden(code))
      .then((data) => { if (!cancelled) setGarden(data); })
      .catch((error) => {
        if (cancelled) return;
        setMissing(true);
        setUnavailable(isUnavailable(error));
        setOurs(isServerFailure(error) ? error : null);
      });
    return () => { cancelled = true; };
    // handed is read once, on arrival; a later visit asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, slug, notAnAddress]);

  // The gardener has an address of their own: the page lives there, so the
  // visitor is moved on, the code replaced in their history. An address
  // typed in capitals or with accents is put right the same way.
  React.useEffect(() => {
    if (!garden?.slug) return;
    if (slug === undefined || typedSlug !== garden.slug) {
      navigate(gardenPathFor(garden.code, garden.slug), { replace: true, state: { garden } });
    }
  }, [garden, slug, typedSlug, navigate]);

  // The garden is the credit for a sign-up from here, as a share is from its page.
  useGardenCredit(garden?.code, garden?.gardener?.name);

  // A path with no "@", or an address no one has, goes where any unknown path goes.
  if (notAnAddress || (slug !== undefined && missing && !unavailable && !ours)) return <Navigate to="/today" replace />;

  const empty = garden !== null && garden.beds.length === 0 && garden.plants.length === 0;
  /** The garden's address for its plants' and beds' own pages; none before it has one. */
  const gardenSlug = garden?.slug ?? null;

  return (
    <GardenFrame bare={Boolean(garden)}>
      {missing && (ours
        ? <ServerFailureNote error={ours} where="Garden page" className="text-base text-muted-foreground" />
        : <p className="text-muted-foreground">{t(unavailable ? 'gardenPage.page.unavailable' : 'gardenPage.page.missing')}</p>)}
      {!missing && !garden && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      )}

      {/* Dates as the gardener shows them (Profile > Public pages: the day, the year). */}
      {garden && (
        <DatePartsContext.Provider value={datePartsOf(garden.dates)}>
          <GardenHero garden={garden} />

          <div className={`${GARDEN_COLUMN_CLASS} space-y-8 pb-12 pt-5`}>
            {garden.latest && <LatestShare latest={garden.latest} name={garden.gardener?.name ?? null} />}

            {empty && <p className="text-sm text-muted-foreground">{t('gardenPage.page.empty')}</p>}

            {/* Plants first (what visitors come for), then the beds, then the season review. */}
            {garden.plants.length > 0 && <PlantGrid plants={garden.plants} gardenSlug={gardenSlug} id={PLANTS_SECTION_ID} />}

            {garden.beds.length > 0 && <BedRail beds={garden.beds} gardenSlug={gardenSlug} />}

            {/* The season review's picture, once it went out with its Public switch on. */}
            {garden.review && (
              <Link to={`/s/${garden.review.code}`} className="block overflow-hidden rounded-2xl border bg-card hover:bg-muted">
                <LargePicture
                  src={garden.review.frame}
                  alt={t('seasonReview.page.title', { year: garden.review.year })}
                  kind="frame"
                  size={garden.review.size}
                />
                <span className="block px-3 py-2 text-sm font-medium">{t('seasonReview.page.title', { year: garden.review.year })}</span>
              </Link>
            )}

            <div className="mx-auto max-w-xl pt-4">
              <GardenInvite code={garden.code} name={garden.gardener?.name ?? null} card />
            </div>

            <p className="text-center">
              <Link to="/gardens" className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary underline-offset-4 hover:underline">
                {t('gardensPage.moreGardens')}
              </Link>
            </p>
          </div>
        </DatePartsContext.Provider>
      )}
    </GardenFrame>
  );
};

export default GardenPage;
